import React from 'react';

import camelCase from 'lodash/camelCase';
import type { IntlShape } from 'react-intl';

import type { DispatchAnalyticsEvent } from '@atlaskit/editor-common/analytics/types/dispatch-analytics-event';
import type {
	FLOATING_CONTROLS_TITLE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	CONTENT_COMPONENT,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics/types/enums';
import type { AnalyticsEventPayload } from '@atlaskit/editor-common/analytics/types/events';
import { ErrorBoundary } from '@atlaskit/editor-common/error-boundary';
import { isSSR } from '@atlaskit/editor-common/is-ssr';
import Popup from '@atlaskit/editor-common/Popup';
import type { Position } from '@atlaskit/editor-common/Popup/utils';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import { WithProviders } from '@atlaskit/editor-common/provider-factory/with-providers';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { areToolbarFlagsEnabled } from '@atlaskit/editor-common/toolbar-flag-check';
import type { Command } from '@atlaskit/editor-common/types/command';
import type {
	FloatingToolbarButton,
	FloatingToolbarConfig,
	FloatingToolbarHandler,
	FloatingToolbarOverflowDropdown,
} from '@atlaskit/editor-common/types/floating-toolbar';
import type {
	ExtractInjectionAPI,
	ExtractPluginSharedState,
} from '@atlaskit/editor-common/types/next-editor-plugin';
import type { PMPlugin } from '@atlaskit/editor-common/types/plugin-factory';
import type { UiComponentFactoryParams } from '@atlaskit/editor-common/types/ui-components';
import { useSharedPluginState } from '@atlaskit/editor-common/useSharedPluginState';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Selection } from '@atlaskit/editor-prosemirror/state';
import { AllSelection, PluginKey, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos, findSelectedNodeOfType } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	ConfigWithNodeInfo,
	FloatingToolbarPlugin,
	FloatingToolbarPluginState,
} from './floatingToolbarPluginType';
import { useToolbarExternalStore } from './hooks/useToolbarExternalStore';
import { copyNode } from './pm-plugins/commands';
import forceFocusPlugin, { forceFocusSelector } from './pm-plugins/force-focus';
import { hideConfirmDialog } from './pm-plugins/toolbar-data/commands';
import { createPlugin as floatingToolbarDataPluginFactory } from './pm-plugins/toolbar-data/plugin';
import { pluginKey as dataPluginKey } from './pm-plugins/toolbar-data/plugin-key';
import { findNode } from './pm-plugins/utils';
import { ConfirmationModal } from './ui/ConfirmationModal';
import Toolbar from './ui/Toolbar';
import { consolidateOverflowDropdownItems } from './ui/utils';

const SUPPRESS_TOOLBAR_USER_INTENTS = [
	'dragging',
	'tableContextualMenuPopupOpen',
	'tableDragMenuPopupOpen',
	'commenting',
	// Suppress while reviewing an AI Suggested Edits card so node floating toolbars don't overlap
	// or mis-position against the suggestion card / diff decorations. Set by the ai-suggestions plugin.
	'reviewing',
	'resizing',
	'blockMenuOpen',
	'statusPickerOpen',
	// Suppress when a modal/overlay is open over the editor (e.g. the Jira remix ephemeral preview),
	// so the floating toolbar does not bleed through on top of the overlay.
	'overlayOpen',
	'focusOutsideEditor',
];

// TODO: AFP-2532 - Fix automatic suppressions below
export const getRelevantConfig = (
	selection: Selection,
	configs: Array<FloatingToolbarConfig>,
): ConfigWithNodeInfo | undefined => {
	// node selections always take precedence, see if
	let configPair: ConfigWithNodeInfo | undefined;
	configs.find((config) => {
		const node = findSelectedNodeOfType(config.nodeType)(selection);
		if (node) {
			configPair = {
				node: node.node,
				pos: node.pos,
				config,
			};
		}

		return !!node;
	});

	if (configPair) {
		return configPair;
	}

	// create mapping of node type name to configs
	const configByNodeType: Record<string, FloatingToolbarConfig> = {};
	configs.forEach((config) => {
		if (Array.isArray(config.nodeType)) {
			config.nodeType.forEach((nodeType) => {
				configByNodeType[nodeType.name] = config;
			});
		} else {
			configByNodeType[config.nodeType.name] = config;
		}
	});

	// search up the tree from selection
	const { $from } = selection;
	for (let i = $from.depth; i > 0; i--) {
		const node = $from.node(i);

		const matchedConfig = configByNodeType[node.type.name];
		if (matchedConfig) {
			return { config: matchedConfig, node: node, pos: $from.pos };
		}
	}

	// if it is AllSelection (can be result of Cmd+A) - use first node
	if (selection instanceof AllSelection) {
		const docNode = $from.node(0);

		let matchedConfig: FloatingToolbarConfig | null = null;
		const firstChild = findNode(docNode, (node) => {
			matchedConfig = configByNodeType[node.type.name];
			return !!matchedConfig;
		});
		if (firstChild && matchedConfig) {
			return { config: matchedConfig, node: firstChild, pos: $from.pos };
		}
	}

	return;
};

const getDomRefFromSelection = (
	view: EditorView,
	dispatchAnalyticsEvent?: DispatchAnalyticsEvent,
) => {
	try {
		// Ignored via go/ees005
		// eslint-disable-next-line @atlaskit/editor/no-as-casting
		return findDomRefAtPos(view.state.selection.from, view.domAtPos.bind(view)) as HTMLElement;
		// Ignored via go/ees005
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.warn(error);
		if (dispatchAnalyticsEvent) {
			const payload: AnalyticsEventPayload = {
				action: ACTION.ERRORED,
				actionSubject: ACTION_SUBJECT.CONTENT_COMPONENT,
				eventType: EVENT_TYPE.OPERATIONAL,
				attributes: {
					component: CONTENT_COMPONENT.FLOATING_TOOLBAR,
					selection: view.state.selection.toJSON(),
					position: view.state.selection.from,
					docSize: view.state.doc.nodeSize,
					error: error.toString(),
					// @ts-expect-error - Object literal may only specify known properties, 'errorStack' does not exist in type
					// This error was introduced after upgrading to TypeScript 5
					errorStack: error.stack || undefined,
				},
			};
			dispatchAnalyticsEvent(payload);
		}
	}
};

function filterUndefined<T>(x?: T): x is T {
	return !!x;
}

export const floatingToolbarPlugin: FloatingToolbarPlugin = ({ api }) => {
	let editorView: EditorView | undefined;
	let sharedStateCache:
		| WeakMap<EditorState, ExtractPluginSharedState<FloatingToolbarPlugin>>
		| undefined;
	const getSharedState = (
		editorState: EditorState | undefined,
	): ExtractPluginSharedState<FloatingToolbarPlugin> => {
		if (!editorState) {
			return undefined;
		}

		const memoizePreviousState = isExperimentEnabled('platform_editor_improve_inp');
		if (memoizePreviousState) {
			// Current-state reads remain live: handlers can depend on mutable values
			// outside EditorState. Only historical reads may reuse a computed result.
			if (editorView && editorState !== editorView.state) {
				const cached = sharedStateCache?.get(editorState);
				if (cached) {
					return cached;
				}
			}
		} else {
			sharedStateCache = undefined;
		}

		const interactionState = api?.interaction?.sharedState.currentState()?.interactionState;

		const configWithNodeInfo =
			interactionState !== 'hasNotHadInteraction'
				? (pluginKey.getState(editorState)?.getConfigWithNodeInfo?.(editorState) ?? undefined)
				: undefined;

		const sharedState = {
			configWithNodeInfo,
			floatingToolbarData: dataPluginKey.getState(editorState),
		};
		if (memoizePreviousState && editorView) {
			sharedStateCache ??= new WeakMap();
			sharedStateCache.set(editorState, sharedState);
		}
		return sharedState;
	};

	return {
		name: 'floatingToolbar',

		pmPlugins(floatingToolbarHandlers: Array<FloatingToolbarHandler> = []) {
			const plugins: PMPlugin[] = [
				{
					// Should be after all toolbar plugins
					name: 'floatingToolbar',
					plugin: ({ providerFactory, getIntl }) =>
						floatingToolbarPluginFactory({
							api,
							floatingToolbarHandlers,
							providerFactory,
							getIntl,
						}),
				},
				{
					name: 'floatingToolbarData',
					plugin: ({ dispatch }) => floatingToolbarDataPluginFactory(dispatch),
				},
				{
					name: 'forceFocus',
					plugin: () => forceFocusPlugin(),
				},
			];

			if (isExperimentEnabled('platform_editor_improve_inp')) {
				plugins.push({
					name: 'floatingToolbarSharedStateCache',
					plugin: () =>
						new SafePlugin({
							view: (view) => {
								editorView = view;
								sharedStateCache = undefined;
								return {
									destroy: () => {
										editorView = undefined;
										sharedStateCache = undefined;
									},
								};
							},
						}),
				});
			}

			return plugins;
		},

		actions: {
			forceFocusSelector,
		},
		commands: {
			copyNode: (nodeType: NodeType | NodeType[], inputMethod?: INPUT_METHOD) =>
				copyNode(nodeType, api?.analytics?.actions, inputMethod),
		},

		getSharedState,

		contentComponent({
			popupsMountPoint,
			popupsBoundariesElement,
			popupsScrollableElement,
			editorView,
			providerFactory,
			dispatchAnalyticsEvent,
		}) {
			if (!editorView) {
				return null;
			}

			return (
				<ContentComponent
					editorView={editorView}
					pluginInjectionApi={api}
					popupsMountPoint={popupsMountPoint}
					popupsBoundariesElement={popupsBoundariesElement}
					popupsScrollableElement={popupsScrollableElement}
					providerFactory={providerFactory}
					dispatchAnalyticsEvent={dispatchAnalyticsEvent}
				/>
			);
		},
	};
};

/**
 * React component that renders the floating toolbar UI for the editor.
 *
 * This component manages the display of floating toolbars based on the current editor state,
 * selection, and configuration. It handles visibility conditions, positioning, toolbar items
 * consolidation, and confirmation dialogs.
 *
 * @param props - Component properties
 * @param props.pluginInjectionApi - Plugin injection API for accessing other plugin states
 * @param props.editorView - ProseMirror EditorView instance
 * @param props.popupsMountPoint - DOM element where popups should be mounted
 * @param props.popupsBoundariesElement - Element that defines popup boundaries
 * @param props.popupsScrollableElement - Scrollable container element for popups
 * @param props.providerFactory - Factory for creating various providers
 * @param props.dispatchAnalyticsEvent - Function to dispatch analytics events
 * @returns JSX element representing the floating toolbar or null if not visible
 */
export function ContentComponent({
	pluginInjectionApi,
	editorView,
	popupsMountPoint,
	popupsBoundariesElement,
	popupsScrollableElement,
	providerFactory,
	dispatchAnalyticsEvent,
}: Pick<
	UiComponentFactoryParams,
	| 'editorView'
	| 'popupsMountPoint'
	| 'popupsBoundariesElement'
	| 'providerFactory'
	| 'dispatchAnalyticsEvent'
	| 'popupsScrollableElement'
> & {
	pluginInjectionApi: ExtractInjectionAPI<FloatingToolbarPlugin> | undefined;
} & { editorView: EditorView }): React.JSX.Element | null {
	const { floatingToolbarState, editorDisabledState, editorViewModeState, userIntentState } =
		useSharedPluginState(pluginInjectionApi, [
			'floatingToolbar',
			'editorDisabled',
			'editorViewMode',
			'userIntent',
		]);

	const { configWithNodeInfo: configWithNodeInfoFromHook, floatingToolbarData } =
		floatingToolbarState ?? {};

	let configWithNodeInfo = configWithNodeInfoFromHook;
	if (fg('platform_editor_ai_remix_toolbar')) {
		// Read configWithNodeInfo live because the hook snapshot can lag behind the
		// current editor selection and keep the floating toolbar rendered stale.
		// for example, trigger remix on panel will not hide the floating toolbar
		configWithNodeInfo =
			pluginInjectionApi?.floatingToolbar?.sharedState.currentState()?.configWithNodeInfo;
	}

	// Subscribe to an external store for rerenders. Used by editor-plugin-card for card registration via editor actions
	useToolbarExternalStore(configWithNodeInfo?.config?.externalStore);

	if (isSSR()) {
		return null;
	}

	if (
		!configWithNodeInfo ||
		!configWithNodeInfo.config ||
		(typeof configWithNodeInfo.config?.visible !== 'undefined' &&
			!configWithNodeInfo.config?.visible)
	) {
		return null;
	}

	if (
		userIntentState?.currentUserIntent &&
		SUPPRESS_TOOLBAR_USER_INTENTS.includes(userIntentState?.currentUserIntent)
	) {
		return null;
	}

	const { config, node } = configWithNodeInfo;

	// When the new inline editor-toolbar is enabled, suppress floating toolbar for text selections.
	if (Boolean(pluginInjectionApi?.toolbar)) {
		const selection = editorView.state.selection as Selection;
		const isCellSelection = '$anchorCell' in selection && !selection.empty;
		const isTextSelected = selection instanceof TextSelection && !selection.empty;

		// don't dismiss table toolbar when a cell selection is caused by clicking the drag handle (which has it's own userIntent)
		if (
			(isTextSelected && config.className !== 'hyperlink-floating-toolbar') ||
			(isCellSelection && userIntentState?.currentUserIntent === 'default')
		) {
			return null;
		}
	}

	let { items } = config;
	const { groupLabel } = config;
	const {
		title,
		getDomRef = getDomRefFromSelection,
		align = 'center',
		className = '',
		containerSurface,
		height,
		width,
		zIndex,
		offset = [0, 12],
		forcePlacement,
		preventPopupOverflow,
		onPositionCalculated,
		absoluteOffset = { top: 0, left: 0, right: 0, bottom: 0 },
		focusTrap,
		mediaAssistiveMessage = '',
		stick = true,
	} = config;
	const targetRef = getDomRef(editorView, dispatchAnalyticsEvent);

	const isEditorDisabled = editorDisabledState && editorDisabledState.editorDisabled;
	const isInViewMode = editorViewModeState?.mode === 'view';
	if (!targetRef || (isEditorDisabled && !isInViewMode)) {
		return null;
	}

	// TODO: MODES-3950 - Update this view mode specific logic once we refactor view mode.
	//       We should inverse the responsibility here: A blacklist for toolbar items in view mode, rather than this white list.
	//       Also consider moving this logic to the more specific toolbar plugins (media and selection).
	const iterableItems = Array.isArray(items) ? items : items?.(node);
	if (isInViewMode) {
		// Typescript note: Not all toolbar item types have the `supportsViewMode` prop.
		const toolbarItemViewModeProp: keyof FloatingToolbarButton<Command> = 'supportsViewMode';
		// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render -- Ignored via go/ees017 (to be fixed)
		items = iterableItems.filter(
			(item) => toolbarItemViewModeProp in item && !!item[toolbarItemViewModeProp],
		);
	}

	if (areToolbarFlagsEnabled(Boolean(pluginInjectionApi?.toolbar))) {
		// Consolidate floating toolbar items
		const toolbarItemsArray = Array.isArray(items) ? items : items?.(node);
		// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render -- Ignored via go/ees017 (to be fixed)
		const overflowDropdownItems = toolbarItemsArray.filter(
			(item) => item.type === 'overflow-dropdown',
		) as FloatingToolbarOverflowDropdown<Command>[];

		if (overflowDropdownItems.length > 1) {
			const consolidatedOverflowDropdown = consolidateOverflowDropdownItems(overflowDropdownItems);
			// eslint-disable-next-line @atlassian/perf-linting/no-expensive-computations-in-render -- Ignored via go/ees017 (to be fixed)
			const otherItems = toolbarItemsArray.filter((item) => item.type !== 'overflow-dropdown');

			if (otherItems.length > 0) {
				// remove the last separators
				while (otherItems.at(-1)?.type === 'separator') {
					otherItems.pop();
				}
			}

			items = [
				...otherItems,
				{ type: 'separator', fullHeight: true, supportsViewMode: true },
				consolidatedOverflowDropdown,
			];
		}

		// Apply analytics to dropdown
		if (overflowDropdownItems.length > 0 && dispatchAnalyticsEvent) {
			const currentItems = Array.isArray(items) ? items : items?.(node);
			const updatedItems = currentItems.map((item) => {
				if (item.type !== 'overflow-dropdown') {
					return item;
				}
				const originalOnClick = item.onClick;
				return {
					...item,
					onClick: () => {
						const editorContentMode =
							pluginInjectionApi?.editorViewMode?.sharedState.currentState()?.mode;
						dispatchAnalyticsEvent({
							action: ACTION.CLICKED,
							actionSubject: ACTION_SUBJECT.BUTTON,
							actionSubjectId: ACTION_SUBJECT_ID.FLOATING_TOOLBAR_OVERFLOW,
							eventType: EVENT_TYPE.UI,
							attributes: {
								editorContentMode,
							},
						});
						// Call original onClick if it exists
						originalOnClick?.();
					},
				};
			});
			items = updatedItems;
		}
	}

	let customPositionCalculation;
	const toolbarItems = pluginInjectionApi?.copyButton?.actions.processCopyButtonItems(
		editorView.state,
	)(
		Array.isArray(items) ? items : items(node),
		pluginInjectionApi?.decorations?.actions.hoverDecoration,
	);

	if (onPositionCalculated) {
		customPositionCalculation = (nextPos: Position): Position => {
			return onPositionCalculated(editorView, nextPos);
		};
	}

	const dispatchCommand = (fn?: Function) =>
		fn && fn(editorView.state, editorView.dispatch, editorView);

	// Confirm dialog
	let confirmButtonItem;

	const { confirmDialogForItem, confirmDialogForItemOption } = floatingToolbarData || {};
	const matchingItem = confirmDialogForItem ? toolbarItems?.[confirmDialogForItem] : undefined;

	if (matchingItem?.type === 'button') {
		confirmButtonItem = matchingItem;
	}

	if (matchingItem?.type === 'overflow-dropdown' && confirmDialogForItemOption !== undefined) {
		const matchingItemOption = matchingItem.options[confirmDialogForItemOption];

		// OverflowDropdownOption is the only member of the union that does not have a 'type' property
		if (!('type' in matchingItemOption)) {
			confirmButtonItem = matchingItemOption;
		}
	}

	const scrollable = config.scrollable;

	const confirmDialogOptions =
		typeof confirmButtonItem?.confirmDialog === 'function'
			? confirmButtonItem?.confirmDialog()
			: confirmButtonItem?.confirmDialog;

	return (
		<ErrorBoundary
			component={ACTION_SUBJECT.FLOATING_TOOLBAR_PLUGIN}
			componentId={camelCase(title) as FLOATING_CONTROLS_TITLE}
			dispatchAnalyticsEvent={dispatchAnalyticsEvent}
			fallbackComponent={null}
		>
			<Popup
				ariaLabel={title}
				role={'toolbar'}
				offset={offset}
				target={targetRef}
				alignY="bottom"
				forcePlacement={forcePlacement}
				fitHeight={height}
				fitWidth={width}
				absoluteOffset={absoluteOffset}
				alignX={align}
				stick={stick}
				zIndex={zIndex}
				mountTo={popupsMountPoint}
				boundariesElement={popupsBoundariesElement}
				scrollableElement={popupsScrollableElement}
				onPositionCalculated={customPositionCalculation}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={scrollable ? { maxWidth: '100%' } : {}}
				focusTrap={focusTrap}
				preventOverflow={preventPopupOverflow}
			>
				<WithProviders
					providerFactory={providerFactory}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					providers={['extensionProvider']}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					renderNode={(providers) => {
						return (
							<Toolbar
								target={targetRef}
								// Ignored via go/ees005
								// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
								items={toolbarItems!}
								groupLabel={groupLabel}
								node={node}
								dispatchCommand={dispatchCommand}
								editorView={editorView}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
								className={className}
								containerSurface={containerSurface}
								// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
								focusEditor={() => editorView.focus()}
								providerFactory={providerFactory}
								popupsMountPoint={popupsMountPoint}
								popupsBoundariesElement={popupsBoundariesElement}
								popupsScrollableElement={popupsScrollableElement}
								dispatchAnalyticsEvent={dispatchAnalyticsEvent}
								extensionsProvider={providers.extensionProvider}
								scrollable={scrollable}
								api={pluginInjectionApi}
								mediaAssistiveMessage={mediaAssistiveMessage}
							/>
						);
					}}
				/>
			</Popup>

			<ConfirmationModal
				testId="ak-floating-toolbar-confirmation-modal"
				options={confirmDialogOptions}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onConfirm={(isChecked = false) => {
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					if (!!confirmDialogOptions!.onConfirm) {
						// Ignored via go/ees005
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						dispatchCommand(confirmDialogOptions!.onConfirm(isChecked));
					} else {
						// Ignored via go/ees005
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						dispatchCommand(confirmButtonItem!.onClick);
					}
				}}
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				onClose={() => {
					dispatchCommand(hideConfirmDialog());
					// Need to set focus to Editor here,
					// As when the Confirmation dialog pop up, and user interacts with the dialog, Editor loses focus.
					// So when Confirmation dialog is closed, Editor does not have the focus, then cursor goes to the position 1 of the doc,
					// instead of the cursor position before the dialog pop up.
					if (!editorView.hasFocus()) {
						editorView.focus();
					}
				}}
			/>
		</ErrorBoundary>
	);
}

/**
 *
 * ProseMirror Plugin
 *
 */
// We throttle update of this plugin with RAF.
// So from other plugins you will always get the previous state.
export const pluginKey: PluginKey<FloatingToolbarPluginState> =
	new PluginKey<FloatingToolbarPluginState>('floatingToolbarPluginKey');

/**
 * Clean up floating toolbar configs from undesired properties.
 */
function sanitizeFloatingToolbarConfig(config: FloatingToolbarConfig): FloatingToolbarConfig {
	// Cleanup from non existing node types
	if (Array.isArray(config.nodeType)) {
		return {
			...config,
			nodeType: config.nodeType.filter(filterUndefined),
		};
	}

	return config;
}

/**
 * Creates a floating toolbar plugin for the ProseMirror editor.
 *
 * This factory function creates a SafePlugin that manages floating toolbars in the editor.
 * It processes an array of floating toolbar handlers and determines which toolbar configuration
 * should be active based on the current editor state and selection.
 *
 * @param options - Configuration object for the floating toolbar plugin
 * @param options.floatingToolbarHandlers - Array of handlers that return toolbar configurations
 * @param options.getIntl - Function that returns the IntlShape instance for internationalization
 * @param options.providerFactory - Factory for creating various providers used by the editor
 * @returns A SafePlugin instance that manages floating toolbar state and behavior
 */
export function floatingToolbarPluginFactory(options: {
	api: ExtractInjectionAPI<FloatingToolbarPlugin> | undefined;
	floatingToolbarHandlers: Array<FloatingToolbarHandler>;
	getIntl: () => IntlShape;
	providerFactory: ProviderFactory;
}): SafePlugin<{
	getConfigWithNodeInfo: (editorState: EditorState) => ConfigWithNodeInfo | null | undefined;
}> {
	const { floatingToolbarHandlers, providerFactory, getIntl, api } = options;
	const intl = getIntl();
	const getConfigWithNodeInfo = (
		editorState: EditorState,
	): ConfigWithNodeInfo | null | undefined => {
		let activeConfigs: Array<FloatingToolbarConfig> | undefined = [];

		for (let index = 0; index < floatingToolbarHandlers.length; index++) {
			const handler = floatingToolbarHandlers[index];
			const config = handler(editorState, intl, providerFactory, activeConfigs);
			if (config) {
				if (
					SUPPRESS_TOOLBAR_USER_INTENTS.includes(
						api?.userIntent?.sharedState.currentState()?.currentUserIntent || '',
					)
				) {
					activeConfigs = undefined;
					break;
				}

				activeConfigs.push(sanitizeFloatingToolbarConfig(config));
			}
		}

		const relevantConfig = activeConfigs && getRelevantConfig(editorState.selection, activeConfigs);
		return relevantConfig;
	};

	const getIsToolbarSuppressed = () => {
		return false;
	};

	return new SafePlugin({
		key: pluginKey,
		state: {
			init: () => {
				return { getConfigWithNodeInfo };
			},
			apply: (_tr, _pluginState, __oldEditorState) => {
				const suppressedToolbar = getIsToolbarSuppressed();

				const newPluginState: FloatingToolbarPluginState = {
					getConfigWithNodeInfo,
					suppressedToolbar,
				};

				return newPluginState;
			},
		},
		view: () => {
			return {
				update: (view, prevState) => {
					const pluginState = pluginKey.getState(view.state);
					const prevPluginState = pluginKey.getState(prevState);

					if (pluginState?.suppressedToolbar && !prevPluginState?.suppressedToolbar) {
						api?.analytics?.actions?.fireAnalyticsEvent({
							action: ACTION.SUPPRESSED,
							actionSubject: ACTION_SUBJECT.FLOATING_TOOLBAR_PLUGIN,
							actionSubjectId: ACTION_SUBJECT_ID.FLOATING_TOOLBAR,
							eventType: EVENT_TYPE.TRACK,
						});
					}
				},
			};
		},
	});
}
